import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const QUOTE_ENDPOINT = process.env.REACT_APP_QUOTE_ENDPOINT || "/api/quotes.php";
const initial = { name: "", company: "", email: "", phone: "", message: "", website: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone || !form.message) {
      toast.error("Por favor preencha todos os campos.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(QUOTE_ENDPOINT, form, {
        headers: { "Content-Type": "application/json" },
      });
      const reference = response?.data?.reference;
      toast.success("Pedido recebido!", {
        description: reference
          ? `Referência ${reference}. Enviámos uma confirmação para o seu email.`
          : "Enviámos uma confirmação para o seu email.",
      });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Não foi possível enviar.", {
        description: typeof detail === "string" ? detail : "Tente novamente em alguns instantes.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contactos" className="py-24 lg:py-32 bg-muted/40 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#00AEEF]/8 via-[#EC008C]/6 to-[#FF7F00]/6 blur-3xl pointer-events-none" />

      <div className="relative w-full px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-4">Contactos</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold text-foreground tracking-tight leading-[1.05]" data-testid="contact-title">
            Peça o Seu <span className="cmyk-text">Orçamento</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Conte-nos as necessidades da sua organização. Respondemos em 24h com uma proposta clara, competitiva e adaptada.
          </p>

          <div className="mt-10 space-y-5">
            <InfoItem icon={Phone} label="Telefone" value="(+351) 253 532 666" testid="contact-phone" />
            <InfoItem icon={Mail} label="Email" value="geral@ecoption.pt" testid="contact-email" />
            <InfoItem icon={MapPin} label="Morada" value={<>Rua de São Miguel, 716<br />Creixomil · 4835-060 Guimarães</>} testid="contact-location" />
            <InfoItem icon={Clock} label="Horário" value={<>Seg–Sex · 09h00–13h00<br />e 15h00–19h00</>} testid="contact-hours" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={submit}
            className="bg-card border border-border rounded-2xl p-7 lg:p-10 shadow-[0_24px_60px_-20px_hsl(var(--foreground)/0.18)]"
            data-testid="quote-form"
          >
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website" name="website" value={form.website} onChange={onChange}
                tabIndex={-1} autoComplete="off"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nome" name="name" value={form.name} onChange={onChange} placeholder="O seu nome" testid="form-name" />
              <Field label="Empresa" name="company" value={form.company} onChange={onChange} placeholder="Nome da empresa" testid="form-company" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="email@empresa.pt" testid="form-email" />
              <Field label="Telefone" name="phone" value={form.phone} onChange={onChange} placeholder="+351 XXX XXX XXX" testid="form-phone" />
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Mensagem</Label>
              <Textarea
                id="message" name="message" value={form.message} onChange={onChange} rows={5}
                placeholder="Descreva o que necessita: tipo de consumíveis, quantidades estimadas, recorrência, reparações, etc."
                className="mt-2 bg-background border-border rounded-lg focus-visible:ring-foreground"
                data-testid="form-message"
              />
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">Ao submeter aceita ser contactado pela Ecoption Supply para resposta ao seu pedido.</p>
              <Button
                type="submit" disabled={loading}
                className="group bg-[#0A1F44] hover:bg-[#112E5C] dark:bg-white dark:hover:bg-slate-200 dark:text-[#0A1F44] text-white rounded-full px-7 h-12 font-semibold disabled:opacity-60"
                data-testid="form-submit"
              >
                {loading ? "A enviar..." : "Solicitar Orçamento"}
                {!loading && <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label htmlFor={name} className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</Label>
      <Input
        id={name} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="mt-2 bg-background border-border rounded-lg h-11 focus-visible:ring-foreground"
        data-testid={testid}
      />
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <div className="h-11 w-11 rounded-lg bg-card border border-border flex items-center justify-center text-foreground flex-shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</div>
        <div className="font-display text-base lg:text-lg font-semibold text-foreground leading-snug">{value}</div>
      </div>
    </div>
  );
}
